/* eslint-disable import/no-cycle */
import { call, put, select, takeLatest, takeLeading } from 'redux-saga/effects';
import { toast } from 'react-toastify';
import * as types from '../../merchantRegistration/actionTypes';
import {
  submitMerchantRegistrationSuccess,
  submitMerchantRegistrationFailure,
  submitAdministrativeBillingDetailsSuccess,
  submitAdministrativeBillingDetailsFailure,
  getAdministrativeBillingDetailsSuccess,
  getAdministrativeBillingDetailsFailure,
  sendContractOtpSuccess,
  sendContractOtpFailure,
  verifyContractOtpSuccess,
  verifyContractOtpFailure,
  getContractSuccess,
  getContractFailure,
  getMerchantPaymentRequiredInfo,
  getMerchantPaymentRequiredInfoSuccess,
  getMerchantPaymentRequiredInfoFailure,
  verifyMerchantCheckoutSessionSuccess,
  verifyMerchantCheckoutSessionFailure,
  clearMerchantCheckoutVerifySession,
} from '../../merchantRegistration/actions';
import {
  submitMerchantRegistrationApi,
  submitAdministrativeBillingDetailsApi,
  getAdministrativeBillingDetailsApi,
  sendContractOtpApi,
  verifyContractOtpApi,
  getContractApi,
  getMerchantPaymentRequiredInfoApi,
  verifyMerchantCheckoutSessionApi,
} from '../requests/merchantRegistrationRequest';
import { startLoader, stopLoader } from '../../../utility/commonFunction';
import { getMe } from '../../authentication/actions';

function* submitMerchantRegistrationHandler(action) {
  try {
    yield startLoader('transparent');
    const { data } = yield call(submitMerchantRegistrationApi, action.payload);
    if (data?.status) {
      yield put(submitMerchantRegistrationSuccess(data?.data));
      yield toast.success(data?.message || 'Merchant registration submitted successfully');
    } else {
      const errors = data?.errors ?? (data?.form_errors ? { form_errors: data.form_errors } : null);
      const errorMessage = errors?.message || errors?.form_errors || 'Failed to submit merchant registration';
      yield put(submitMerchantRegistrationFailure(errors));
      yield toast.error(errorMessage);
    }
  } catch (error) {
    const responseData = error?.response?.data;
    const formErrors = responseData?.form_errors ?? responseData?.errors?.form_errors;
    const failurePayload = formErrors ? { form_errors: formErrors } : responseData?.errors ?? error;
    const message =
      responseData?.message ||
      responseData?.errors?.message ||
      error?.message ||
      'Failed to submit merchant registration';
    yield put(submitMerchantRegistrationFailure(failurePayload));
    yield toast.error(message);
  } finally {
    yield stopLoader('transparent');
  }
}

const ADMINISTRATIVE_BILLING_DETAILS_SAVED_SUCCESS_DEFAULT =
  'Administrative & Billing Details saved successfully.';

function* submitAdministrativeBillingDetailsHandler(action) {
  try {
    const { data } = yield call(submitAdministrativeBillingDetailsApi, action.payload);
    if (data?.status) {
      yield put(submitAdministrativeBillingDetailsSuccess(data?.data));
      const language = yield select((state) => state?.auth?.language);
      const successMessage =
        data?.message ||
        language?.content?.ADMINISTRATIVE_BILLING_DETAILS_SAVED_SUCCESS ||
        ADMINISTRATIVE_BILLING_DETAILS_SAVED_SUCCESS_DEFAULT;
      yield toast.success(successMessage);
    } else {
      const errors = data?.errors ?? (data?.form_errors ? { form_errors: data.form_errors } : null);
      const errorMessage = errors?.message || errors?.form_errors || 'Failed to submit administrative billing details';
      yield put(submitAdministrativeBillingDetailsFailure(errors));
      yield toast.error(errorMessage);
    }
  } catch (error) {
    const responseData = error?.response?.data;
    const formErrors = responseData?.form_errors ?? responseData?.errors?.form_errors;
    const failurePayload = formErrors ? { form_errors: formErrors } : responseData?.errors ?? error;
    const message =
      responseData?.message ||
      responseData?.errors?.message ||
      error?.message ||
      'Failed to submit administrative billing details';
    yield put(submitAdministrativeBillingDetailsFailure(failurePayload));
    yield toast.error(message);
  }
}

function* getAdministrativeBillingDetailsHandler() {
  try {
    const { data } = yield call(getAdministrativeBillingDetailsApi);
    if (data?.status && data?.data) {
      yield put(getAdministrativeBillingDetailsSuccess(data.data));
    } else {
      yield put(getAdministrativeBillingDetailsSuccess(null));
    }
  } catch (error) {
    yield put(
      getAdministrativeBillingDetailsFailure(error?.response?.data || error?.message || 'Failed to load details'),
    );
  }
}

function* sendContractOtpHandler() {
  try {
    yield startLoader('transparent');
    const { data } = yield call(sendContractOtpApi);
    if (data?.status) {
      yield put(sendContractOtpSuccess());
      yield toast.success(data?.message || 'OTP sent to your email');
    } else {
      const errors = data?.errors ?? (data?.form_errors ? { form_errors: data.form_errors } : null);
      const errorMessage = errors?.message || errors?.form_errors || 'Failed to send OTP';
      yield put(sendContractOtpFailure(errors));
      yield toast.error(errorMessage);
    }
  } catch (error) {
    const responseData = error?.response?.data;
    const formErrors = responseData?.form_errors ?? responseData?.errors?.form_errors;
    const failurePayload = formErrors ? { form_errors: formErrors } : responseData?.errors ?? error;
    const message = responseData?.message || responseData?.errors?.message || error?.message || 'Failed to send OTP';
    yield put(sendContractOtpFailure(failurePayload));
    yield toast.error(message);
  } finally {
    yield stopLoader('transparent');
  }
}

function* verifyContractOtpHandler(action) {
  try {
    yield startLoader('transparent');
    const { data } = yield call(verifyContractOtpApi, action.payload);
    if (data?.status) {
      yield put(verifyContractOtpSuccess());
      yield toast.success(data?.message || 'OTP verified successfully');
      // Drop stale Stripe verify flags so payment-required-info is not skipped (persisted redux / prior session)
      yield put(clearMerchantCheckoutVerifySession());
      yield put(getMe());
      yield put(getMerchantPaymentRequiredInfo());
    } else {
      const errors = data?.errors ?? (data?.form_errors ? { form_errors: data.form_errors } : null);
      const errorMessage = errors?.message || errors?.form_errors || 'Failed to verify OTP';
      yield put(verifyContractOtpFailure(errors));
      yield toast.error(errorMessage);
    }
  } catch (error) {
    const responseData = error?.response?.data;
    const formErrors = responseData?.form_errors ?? responseData?.errors?.form_errors;
    const failurePayload = formErrors ? { form_errors: formErrors } : responseData?.errors ?? error;
    const message = responseData?.message || responseData?.errors?.message || error?.message || 'Failed to verify OTP';
    yield put(verifyContractOtpFailure(failurePayload));
    yield toast.error(message);
  } finally {
    yield stopLoader('transparent');
  }
}

function* getContractHandler(action) {
  const lang = action.payload?.lang;
  try {
    const response = yield call(getContractApi, lang);
    const blob = response?.data;
    if (blob instanceof Blob) {
      yield put(getContractSuccess(blob));
    } else {
      yield put(getContractFailure('Invalid contract response'));
      yield toast.error('Failed to load contract');
    }
  } catch (error) {
    const message = error?.response?.data?.message || error?.message || 'Failed to load contract';
    yield put(getContractFailure(message));
    yield toast.error(message);
  }
}

function* getMerchantPaymentRequiredInfoHandler() {
  try {
    const verifyData = yield select((s) => s?.merchantRegistration?.verifyCheckoutSessionData);
    // User already completed checkout + onboarding — skip API but MUST clear loading (reducer only clears on SUCCESS/FAILURE).
    if (verifyData?.paid === true && verifyData?.onboarding_completed === true) {
      yield put(
        getMerchantPaymentRequiredInfoSuccess({
          payment_required: false,
          checkout_url: null,
        }),
      );
      return;
    }
    yield startLoader('transparent');
    const { data } = yield call(getMerchantPaymentRequiredInfoApi);
    if (data?.status && data?.data != null) {
      yield put(getMerchantPaymentRequiredInfoSuccess(data.data));
    } else {
      yield put(getMerchantPaymentRequiredInfoFailure(data?.message || 'Failed to load payment info'));
    }
  } catch (error) {
    const message = error?.response?.data?.message || error?.message || 'Failed to load payment info';
    yield put(getMerchantPaymentRequiredInfoFailure(message));
    yield toast.error(message);
  } finally {
    yield stopLoader('transparent');
  }
}

function* verifyMerchantCheckoutSessionHandler(action) {
  try {
    yield startLoader('transparent');
    const { data } = yield call(verifyMerchantCheckoutSessionApi, action.payload);
    if (data?.status === true) {
      yield put(verifyMerchantCheckoutSessionSuccess(data?.data));
      yield put(getMe());
    } else {
      yield put(verifyMerchantCheckoutSessionFailure(data?.message || 'Payment verification failed'));
    }
  } catch (error) {
    const message = error?.response?.data?.message || error?.message || 'Payment verification failed';
    yield put(verifyMerchantCheckoutSessionFailure(message));
    yield toast.error(message);
  } finally {
    yield stopLoader('transparent');
  }
}

export default function* merchantRegistrationSaga() {
  yield takeLatest(types.SUBMIT_MERCHANT_REGISTRATION, submitMerchantRegistrationHandler);
  yield takeLatest(types.SUBMIT_ADMINISTRATIVE_BILLING_DETAILS, submitAdministrativeBillingDetailsHandler);
  yield takeLatest(types.GET_ADMINISTRATIVE_BILLING_DETAILS, getAdministrativeBillingDetailsHandler);
  yield takeLatest(types.SEND_CONTRACT_OTP, sendContractOtpHandler);
  yield takeLatest(types.VERIFY_CONTRACT_OTP, verifyContractOtpHandler);
  yield takeLatest(types.GET_CONTRACT, getContractHandler);
  // takeLeading: duplicate dispatches (e.g. contract OTP + payment page mount) must not cancel mid-flight — that left paymentRequiredInfoLoading stuck true.
  yield takeLeading(types.GET_MERCHANT_PAYMENT_REQUIRED_INFO, getMerchantPaymentRequiredInfoHandler);
  yield takeLatest(types.VERIFY_MERCHANT_CHECKOUT_SESSION, verifyMerchantCheckoutSessionHandler);
}
